import classNames from "classnames";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Popup } from "semantic-ui-react";

import { useCan } from "components";
import { typedQueryClient } from "hooks";
import { useGetEnrollmentLink, useGetOCWVehicleStatus, useGetServiceLink } from "modules/AppointmentDetails/components/OCW/hooks/useGetOcwData";
import "modules/AppointmentDetails/components/OCW/OCW.scss";
import { AppointmentDetailsKeys } from "modules/AppointmentDetails/queryKeys";

import type { Brand, Dealer, DealerLocation } from "models";

export type OCWButtonProps = {
  appointmentId: number;
  ocwEnabled: boolean;
};

export enum OCW_BUTTON_STATUS {
  NotConnectCapable = 1,
  WorkshopModeOn,
  WorkshopModeOff,
  NotEnrolled
}

export const isPonVehicle = (carBrands: Brand[], vinNr: string) =>
  carBrands.some(brand => brand.pon_brand_code && brand.vin_prefix && vinNr.startsWith(brand.vin_prefix));

export const isPonOcwAvailable = (carBrands: Brand[], vinNr: string, location?: DealerLocation, dealer?: Dealer) => {
  if (!location || !dealer) return false;
  return Boolean(location.pon_location_number && dealer.pon_username && dealer.has_pon_password) && isPonVehicle(carBrands, vinNr);
};

export const OCWButton = ({ appointmentId, ocwEnabled }: OCWButtonProps) => {
  const { t } = useTranslation();
  const {
    data: enrollmentLink,
    refetch: refetchEnrollmentLink,
    isLoading: isEnrollmentLinkLoading,
    isSuccess: isEnrollmentLinkSuccess
  } = useGetEnrollmentLink({ appointment_id: appointmentId });
  const {
    data: serviceLink,
    refetch: refetchServiceLink,
    isLoading: isServiceLinkLoading,
    isSuccess: isServiceLinkSuccess
  } = useGetServiceLink({ appointment_id: appointmentId });
  const { data: ocwVehicleStatusData, isLoading: isOCWDataLoading, refetch: refetchOCWVehicleStatusData } = useGetOCWVehicleStatus({ appointment_id: appointmentId });
  const [ocwButtonStatus, setOcwButtonStatus] = useState<OCW_BUTTON_STATUS | null>(null);
  const canListOCWVehicleStatus = useCan("list", "pon-ocw-vehicle-status");
  const isOCWButtonDisabled = !ocwEnabled || !ocwVehicleStatusData?.is_connect_capable || isEnrollmentLinkLoading || isServiceLinkLoading || isOCWDataLoading;

  useEffect(() => {
    if (!ocwEnabled || !ocwVehicleStatusData?.is_connect_capable) return setOcwButtonStatus(OCW_BUTTON_STATUS.NotConnectCapable);

    if (ocwVehicleStatusData.workshop_mode) return setOcwButtonStatus(OCW_BUTTON_STATUS.WorkshopModeOn);

    if (ocwVehicleStatusData.enrolled) return setOcwButtonStatus(OCW_BUTTON_STATUS.WorkshopModeOff);

    setOcwButtonStatus(OCW_BUTTON_STATUS.NotEnrolled);
  }, [ocwEnabled, ocwVehicleStatusData]);

  const ocwButtonClassName = classNames({
    "grey-ocw-button": ocwButtonStatus === OCW_BUTTON_STATUS.NotConnectCapable || !ocwButtonStatus,
    "orange-ocw-button": ocwButtonStatus === OCW_BUTTON_STATUS.WorkshopModeOn,
    "green-ocw-button": ocwButtonStatus === OCW_BUTTON_STATUS.WorkshopModeOff,
    "red-ocw-button": ocwButtonStatus === OCW_BUTTON_STATUS.NotEnrolled
  });

  const getIconColor = () => {
    switch (ocwButtonStatus) {
      case OCW_BUTTON_STATUS.NotEnrolled:
        return "red";
      case OCW_BUTTON_STATUS.WorkshopModeOn:
        return "orange";
      case OCW_BUTTON_STATUS.WorkshopModeOff:
        return "green";
      case OCW_BUTTON_STATUS.NotConnectCapable:
      default:
        return "grey";
    }
  };

  const getButtonHoverText = () => {
    switch (ocwButtonStatus) {
      case OCW_BUTTON_STATUS.NotConnectCapable:
        return t("v8_not_connect_capable", "Not connect capable");

      case OCW_BUTTON_STATUS.WorkshopModeOn:
        return t("v8_workshop_mode_on", "Workshop mode on");

      case OCW_BUTTON_STATUS.WorkshopModeOff:
        return t("v8_workshop_mode_off", "Workshop mode off");

      case OCW_BUTTON_STATUS.NotEnrolled:
        return t("v8_not_enrolled", "Not enrolled");

      default:
        return "";
    }
  };

  const handleOCWClick = () => {
    if (!ocwEnabled || !ocwVehicleStatusData?.is_connect_capable) return;

    if (!ocwVehicleStatusData.enrolled) {
      refetchEnrollmentLink();
      return;
    }

    refetchServiceLink();
    return;
  };

  useEffect(() => {
    if (isServiceLinkSuccess && serviceLink) {
      window.open(serviceLink, "_blank");
      return;
    }
  }, [isServiceLinkSuccess, serviceLink]);

  useEffect(() => {
    if (isEnrollmentLinkSuccess && enrollmentLink) {
      window.open(enrollmentLink, "_blank");
      return;
    }
  }, [isEnrollmentLinkSuccess, enrollmentLink]);

  useEffect(() => {
    if (canListOCWVehicleStatus) refetchOCWVehicleStatusData();

    return () => {
      typedQueryClient.resetQueries(AppointmentDetailsKeys.ponOCWServiceLink);
      typedQueryClient.resetQueries(AppointmentDetailsKeys.ponOCWEnrollmentLink);
    };
  }, []);

  return (
    <div className="OCW">
      <Popup
        hoverable
        content={
          <>
            {getButtonHoverText()}
            {ocwVehicleStatusData?.mileage && (
              <span>
                <strong>{`${t("v8_mileage", "Mileage")}:`}</strong>
                {ocwVehicleStatusData.mileage} KM
              </span>
            )}
          </>
        }
        trigger={
          <div className="OCW-trigger-wrapper">
            <Button className={ocwButtonClassName} size="small" onClick={handleOCWClick} disabled={isOCWButtonDisabled}>
              <strong className="OCW-label">OCW</strong>
              <Icon className="arrow up right from square OCW-icon" color={getIconColor()} />
            </Button>
          </div>
        }
      />
    </div>
  );
};
